import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { slugify } from '~/utils/formatString';

import { useCreateImpactGroupMutation } from '../graphql/generated/mutations.gql.generated';
import { AllImpactGroupsDocument } from '../graphql/generated/queries.gql.generated';

type Scope = 'account' | 'user';
type Severity = 'info' | 'warning' | 'critical';
type Location = 'dashboard' | 'accountsList' | 'accountDetails';

const MIN_MESSAGE_LENGTH = 8;

/**
 * @description Create Impact Group Dialog
 * @param {Function} onSuccess - The function to call when the impact group is created
 * @returns {JSX.Element} The Create Impact Group Dialog component
 */
export const CreateImpactGroupDialog = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [label, setLabel] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [actionUrl, setActionUrl] = useState<string>('');
  const [templateExpiresAt, setTemplateExpiresAt] = useState<string>('');
  const [scope, setScope] = useState<Scope>('account');
  const [severity, setSeverity] = useState<Severity>('info');
  const [location, setLocation] = useState<Location>('accountDetails');
  const [success, setSuccess] = useState<boolean>(false);

  const [createImpactGroup, { loading, error }] = useCreateImpactGroupMutation({
    refetchQueries: [{ query: AllImpactGroupsDocument }],
    onCompleted: () => {
      setSuccess(true);
      onSuccess?.();
    },
  });

  const handleClose = () => {
    setLabel('');
    setMessage('');
    setDescription('');
    setActionUrl('');
    setTemplateExpiresAt('');
    setScope('account');
    setSeverity('info');
    setLocation('accountDetails');
    setSuccess(false);
    setOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedLabel = label.trim();
    const slug = slugify(trimmedLabel);

    if (!slug) return;

    await createImpactGroup({
      variables: {
        input: {
          impactGroupTemplate: {
            label: trimmedLabel,
            slug,
            notificationMessage: message.trim(),
            scope,
            severity,
            location,
            description: description.trim() || null,
            actionUrl: actionUrl.trim() || null,
            templateExpiresAt: templateExpiresAt ? new Date(templateExpiresAt).toISOString() : null,
          },
        },
      },
    });
  };

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Create Impact Group
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle>Create Impact Group</DialogTitle>

        <DialogContent>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Impact group created successfully!
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          )}

          <TextField
            label="Label"
            required
            fullWidth
            autoFocus
            margin="normal"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={loading || success}
            placeholder="e.g., Trading Restrictions"
            helperText="A human-readable name for this impact group"
          />

          <TextField
            label="Notification Message"
            required
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading || success}
            placeholder="Message shown to users..."
            helperText={`Message shown to users when the impact group is triggered (min ${MIN_MESSAGE_LENGTH} characters)`}
            inputProps={{ minLength: MIN_MESSAGE_LENGTH }}
          />

          <TextField
            label="Description / Remedy (Optional)"
            multiline
            rows={2}
            fullWidth
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading || success}
            placeholder="e.g., Complimentary XFA ([VARIABLE]K)"
            helperText="Internal notes about this impact group (not shown to users)"
          />

          <TextField
            label="Action URL (Optional)"
            fullWidth
            margin="normal"
            value={actionUrl}
            onChange={(e) => setActionUrl(e.target.value)}
            disabled={loading || success}
            placeholder="https://support.topstep.com/article"
            helperText="Optional link for users to learn more or take action"
          />

          <FormControl fullWidth margin="normal" disabled={loading || success}>
            <InputLabel>Scope</InputLabel>
            <Select value={scope} label="Scope" onChange={(e) => setScope(e.target.value as Scope)}>
              <MenuItem value="account">Account - Associated with a specific account</MenuItem>
              <MenuItem value="user">User - Associated with a specific user</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={loading || success}>
            <InputLabel>Location</InputLabel>
            <Select
              value={location}
              label="Location"
              onChange={(e) => setLocation(e.target.value as Location)}
            >
              <MenuItem value="dashboard">Dashboard - Shows on dashboard page</MenuItem>
              <MenuItem value="accountsList">Accounts List - Shows on accounts list page</MenuItem>
              <MenuItem value="accountDetails">
                Account Details - Shows on account details page
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={loading || success}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              label="Severity"
              onChange={(e) => setSeverity(e.target.value as Severity)}
            >
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Template Expires At (Optional)"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={templateExpiresAt}
            onChange={(e) => setTemplateExpiresAt(e.target.value)}
            disabled={loading || success}
            helperText="When this template becomes inactive (optional)"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            {success || error ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !label.trim() || message.trim().length < MIN_MESSAGE_LENGTH}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? 'Creating...' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
